/**
 * WebAppShell — the web app's OWN shell instance.
 *
 * Per epic decision D6, `@nu-assist/ui` ships the surface-agnostic
 * primitives + a default `AppShell` composition; each surface composes
 * its own. This is web's composition: the shared `AppShell` frame with
 * web's own Header (brand + theme/profile actions), LeftMenu, and a
 * slim Footer slotted in. Any slot could fall back to the `ui` default
 * by simply omitting the prop (`AppShell` handles the fallback) — web
 * overrides all three because its chrome diverges from a bare default.
 *
 * The dark-mode toggle is inside `WebProfileMenu` (wired to
 * `useTheme`). This component assumes a `<ThemeProvider>` ancestor
 * (wired in `main.tsx`).
 *
 * Tokenized: composition only — all visual tokens live in the
 * primitives. No inline styles, no arbitrary text sizes here.
 */
import type { ReactNode } from "react";
import { AppShell, Header, Sidebar, Footer } from "@nu-assist/ui";
import { WebHeaderBrand } from "./WebHeader";
import { WebLeftMenu } from "./WebLeftMenu";
import { WebProfileMenu } from "./WebProfileMenu";
import { WebFooterContent } from "./WebFooter";
import { useSidebarCollapse } from "./useSidebarCollapse";
import { PreviewBanner } from "../features/PreviewBanner";
import { ImpersonationBannerSlot } from "../features/ImpersonationBannerSlot";

export interface WebAppShellProps {
  /** Routed page content. */
  children: ReactNode;
  /** Identity for the profile menu header. */
  user: { name: string; email: string; role?: string };
  /** Home affordance (brand click). */
  onHome?: () => void;
  /** Left-menu selection — called with the item's router path. */
  onNavSelect?: (path: string) => void;
  /** Logout handler, surfaced in the profile menu. */
  onLogout?: () => void;
}

export function WebAppShell({
  children,
  user,
  onHome,
  onNavSelect,
  onLogout,
}: WebAppShellProps) {
  const sidebar = useSidebarCollapse();
  return (
    <AppShell
      header={
        <Header
          brand={<WebHeaderBrand onHome={onHome} />}
          actions={
            <WebProfileMenu
              name={user.name}
              email={user.email}
              role={user.role}
              onLogout={onLogout}
            />
          }
        />
      }
      sidebar={
        <Sidebar
          ariaLabel="Primary navigation"
          collapsed={sidebar.collapsed}
          onToggleCollapsed={sidebar.toggle}
        >
          <WebLeftMenu onSelect={onNavSelect} />
        </Sidebar>
      }
      footer={
        <Footer>
          <WebFooterContent />
        </Footer>
      }
    >
      <PreviewBanner />
      <ImpersonationBannerSlot />
      {children}
    </AppShell>
  );
}
